import { useState } from 'react';
import { Mail, MapPin, Phone, Clock, Send, CheckCircle2 } from 'lucide-react';
import { company } from '../../mock';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';

export default function ContactCTA() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({
        title: 'Please add your name and email',
        description: 'We need a way to get back to you.',
      });
      return;
    }
    const serviceLabel = {
      accounting: 'Accounting Services',
      farming: 'Farming & Rural',
      property: 'Property Investments',
      business: 'Business Support',
      other: 'Something else',
    }[form.service] || 'General enquiry';

    const subject = `Website enquiry from ${form.name}`;
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      `Interested in: ${serviceLabel}`,
      '',
      'Message:',
      form.message || '(no message provided)',
      '',
      '— Sent via askawayaccounting.co.nz contact form',
    ].filter(Boolean);
    const body = bodyLines.join('\n');

    const mailtoUrl = `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;

    setSubmitted(true);
    toast({
      title: 'Opening your email app…',
      description: `If nothing opens, email us directly at ${company.email}.`,
    });
    setForm({ name: '', email: '', phone: '', service: '', message: '' });
  };

  return (
    <section id="contact" className="section-anchor py-20 md:py-28 bg-[#f6f3ea]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
        <div className="lg:col-span-2">
          <div className="brand-divider mb-5" />
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-stone-900 leading-tight">
            Have a chat with us today.
          </h2>
          <p className="mt-4 text-stone-600 text-[17px] leading-relaxed">
            Looking for an affordable, experienced accountant in Tararua, Manawatu
            or anywhere across New Zealand? Pop in for a coffee or send us a
            message — the first chat is on us.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-stone-200 p-4">
              <div className="h-11 w-11 rounded-xl bg-[#0b6b5b]/10 grid place-items-center text-[#0b6b5b]">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs text-stone-500">Telephone</div>
                <a
                  href={`tel:${company.phone.replace(/\s/g, '')}`}
                  className="font-display font-semibold text-stone-900 hover:text-[#0b6b5b]"
                >
                  {company.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-stone-200 p-4">
              <div className="h-11 w-11 rounded-xl bg-[#0b6b5b]/10 grid place-items-center text-[#0b6b5b]">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs text-stone-500">Email</div>
                <a
                  href={`mailto:${company.email}`}
                  className="font-display font-semibold text-stone-900 hover:text-[#0b6b5b]"
                >
                  {company.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-stone-200 p-4">
              <div className="h-11 w-11 rounded-xl bg-[#0b6b5b]/10 grid place-items-center text-[#0b6b5b]">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs text-stone-500">Office</div>
                <div className="font-display font-semibold text-stone-900">
                  {company.address}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-stone-200 p-4">
              <div className="h-11 w-11 rounded-xl bg-[#0b6b5b]/10 grid place-items-center text-[#0b6b5b]">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs text-stone-500">Opening Hours</div>
                <ul className="text-sm text-stone-700">
                  {company.hours.map((h) => (
                    <li key={h.day}>
                      <span className="font-medium">{h.day}:</span> {h.time}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-stone-200 p-7 md:p-10 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.25)]">
            {submitted ? (
              <div className="py-10 text-center">
                <div className="h-14 w-14 rounded-full bg-[#0b6b5b]/10 grid place-items-center mx-auto">
                  <CheckCircle2 className="h-7 w-7 text-[#0b6b5b]" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-stone-900">
                  Your message is ready to send!
                </h3>
                <p className="mt-3 text-stone-600">
                  We&apos;ve opened your email app pre-filled with your message. Just hit
                  send to reach us at <strong>{company.email}</strong>.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  className="brand-btn mt-6 rounded-full px-5"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="font-display text-2xl font-semibold text-stone-900">
                  Send us a message
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-stone-700">Your name</Label>
                    <Input
                      id="name"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1.5 h-11 bg-[#fbfaf5] border-stone-300 focus-visible:ring-[#0b6b5b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-stone-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1.5 h-11 bg-[#fbfaf5] border-stone-300 focus-visible:ring-[#0b6b5b]"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-stone-700">Phone (optional)</Label>
                    <Input
                      id="phone"
                      placeholder="021 123 4567"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1.5 h-11 bg-[#fbfaf5] border-stone-300 focus-visible:ring-[#0b6b5b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service" className="text-stone-700">I’m interested in</Label>
                    <Select
                      value={form.service}
                      onValueChange={(v) => setForm({ ...form, service: v })}
                    >
                      <SelectTrigger
                        id="service"
                        className="mt-1.5 h-11 bg-[#fbfaf5] border-stone-300 focus:ring-[#0b6b5b]"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accounting">Accounting Services</SelectItem>
                        <SelectItem value="farming">Farming &amp; Rural</SelectItem>
                        <SelectItem value="property">Property Investments</SelectItem>
                        <SelectItem value="business">Business Support</SelectItem>
                        <SelectItem value="other">Something else</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-stone-700">How can we help?</Label>
                  <Textarea
                    id="message"
                    rows={5}
                    placeholder="Tell us a bit about your business or what you need..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1.5 bg-[#fbfaf5] border-stone-300 focus-visible:ring-[#0b6b5b]"
                  />
                </div>
                <div className="pt-1 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-stone-500">
                    We respect your privacy. Your details stay with us.
                  </p>
                  <Button
                    type="submit"
                    className="brand-btn rounded-full h-12 px-6 font-semibold"
                  >
                    Send message <Send className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
